import React from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useData } from '../../contexts/DataContext';
import { ModulePermissions } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
const menuItems = [{
  id: 'dashboard',
  label: 'Panel de Control',
  icon: LayoutDashboardIcon,
  permission: 'dashboard' as keyof ModulePermissions
}, {
  id: 'patients',
  label: 'Pacientes',
  icon: UsersIcon,
  permission: 'patients' as keyof ModulePermissions
}, {
  id: 'doctors',
  label: 'Doctores',
  icon: UserIcon,
  permission: 'doctors' as keyof ModulePermissions
}, {
  id: 'appointments',
  label: 'Citas',
  icon: CalendarIcon,
  permission: 'appointments' as keyof ModulePermissions
}, {
  id: 'clinical-history',
  label: 'Historial Clínico',
  icon: FileTextIcon,
  permission: 'clinicalHistory' as keyof ModulePermissions
}, {
  id: 'services',
  label: 'Servicios',
  icon: BriefcaseIcon,
  permission: 'services' as keyof ModulePermissions
}, {
  id: 'insurance',
  label: 'Seguros',
  icon: ShieldIcon,
  permission: 'insurance' as keyof ModulePermissions
}, {
  id: 'specialties',
  label: 'Especialidades',
  icon: StethoscopeIcon,
  permission: 'specialties' as keyof ModulePermissions
}, {
  id: 'contacts',
  label: 'Contactos',
  icon: ContactIcon,
  permission: 'contacts' as keyof ModulePermissions
}, {
  id: 'opportunities',
  label: 'Oportunidades',
  icon: TrendingUpIcon,
  permission: 'opportunities' as keyof ModulePermissions
}, {
  id: 'follow-up-notes',
  label: 'Notas de Seguimiento',
  icon: MessageSquareIcon,
  permission: 'followUpNotes' as keyof ModulePermissions
}, {
  id: 'audit-log',
  label: 'Bitácora',
  icon: ClipboardListIcon,
  permission: 'auditLog' as keyof ModulePermissions
}, {
  id: 'settings',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings' as keyof ModulePermissions
}];
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const {
    canView
  } = usePermissions();
  const {
    isLoading
  } = useData();
  const visibleMenuItems = menuItems.filter(item => {
    if (!user) return false;
    // While data is loading, show dashboard and settings as minimum
    // Once loaded, use real permissions
    if (isLoading) {
      return item.id === 'dashboard' || item.id === 'settings';
    }
    return canView(item.permission);
  });
  return <aside className={`h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col fixed left-0 top-0 z-30 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-[68px]' : 'w-64'}`} data-id="element-90">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center min-h-[65px]" data-id="element-91">
        <button onClick={() => onModuleChange('dashboard')} className={`flex items-center gap-3 overflow-hidden hover:opacity-80 transition-opacity ${isCollapsed ? 'justify-center w-full' : ''}`} title="Ir al Panel de Control" data-id="element-92">
          <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-93" />
          {!isCollapsed && <div className="min-w-0 text-left" data-id="element-94">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-95">
                Savidna
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-96">
                CRM Médico
              </p>
            </div>}
        </button>
      </div>

      {/* Toggle Button - debajo del logo */}
      <button onClick={onToggleCollapse} className="ml-3 my-2 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-97">
        {isCollapsed ? <ChevronsRightIcon className="w-4 h-4" data-id="element-98" /> : <ChevronsLeftIcon className="w-4 h-4" data-id="element-99" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2" data-id="element-100">
        <ul className="space-y-1 px-2" data-id="element-101">
          {visibleMenuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentModule === item.id;
          return <li key={item.id} data-id="element-102">
                <button onClick={() => onModuleChange(item.id)} title={isCollapsed ? item.label : undefined} className={`
                    w-full flex items-center gap-3 rounded-lg
                    text-sm font-medium transition-colors relative group
                    ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
                    ${isActive ? 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}
                  `} data-id="element-103">
                  <Icon className="w-5 h-5 flex-shrink-0" data-id="element-104" />
                  {!isCollapsed && <span className="truncate" data-id="element-105">{item.label}</span>}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700" data-id="element-106">
        <button onClick={logout} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors relative group ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-107">
          <LogOutIcon className="w-5 h-5 flex-shrink-0" data-id="element-108" />
          {!isCollapsed && <span data-id="element-109">Cerrar Sesión</span>}
        </button>
      </div>
    </aside>;
}